import { TeamRole } from '@showbie-socrative/socrative-utils';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { sendTeacherIntercomEvent } from '~analytics/intercom';
import { EventTypes } from '~analytics/types';
import {
  UseChangeTeamRole,
  useChangeTeamRole,
} from '~teacher/hook/team/useChangeTeamRole';

interface UseChangeToMemberParams {
  teamId: string;
  targetPublicId: string;
  targetMemberRole: TeamRole;
  onMutate?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  onSettled?: () => void;
}

/**
 * Custom hook to handle the change of a team member's role to Member.
 */
export function useHandleChangeToMember({
  teamId,
  targetPublicId,
  targetMemberRole,
  onMutate,
  onSuccess,
  onError,
  onSettled,
}: UseChangeToMemberParams): {
  handleChangeToMember: () => void;
  changeRoleMutation: UseChangeTeamRole;
} {
  const changeRoleMutation = useChangeTeamRole();

  /**
   * Function to handle the change of a team member's role to Member.
   */
  const handleChangeToMember = () => {
    // Call the onMutate callback if provided
    onMutate?.();

    // Perform the role change mutation
    changeRoleMutation.mutate(
      {
        teamId: teamId,
        memberPublicId: targetPublicId,
        newRole: TeamRole.Member,
      },
      {
        onSuccess: () => {
          // Call the onSuccess callback if provided
          onSuccess?.();

          // Send analytics events
          sendTeacherAmplitudeEvent(EventTypes.TeamsChangeRole, {
            team_id: teamId,
            new_role: TeamRole[TeamRole.Member],
            previous_role: TeamRole[targetMemberRole],
          });
          sendTeacherIntercomEvent(EventTypes.TeamsChangeRole);
        },
        onError: () => {
          // Call the onError callback if provided
          onError?.();
        },
        onSettled: () => {
          // Call the onSettled callback if provided
          onSettled?.();
        },
      }
    );
  };

  return { handleChangeToMember, changeRoleMutation };
}
